'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export interface ContainerModel {
  id: string;
  name: string;
  code: string;
  size: string;
  dimensions: string;
  price: number;
  originalPrice: number;
  savings: number;
  features: string[];
  images: Array<{ url: string; alt: string }>;
  featured: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const containerService = {
  async getAll(): Promise<ContainerModel[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('container_models')
        .select('*')
        .eq('active', true)
        .order('featured', { ascending: false })
        .order('price', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          name: row.name,
          code: row.code,
          size: row.size,
          dimensions: row.dimensions,
          price: parseFloat(row.price),
          originalPrice: parseFloat(row.original_price),
          savings: parseFloat(row.savings),
          features: row.features || [],
          images: row.images || [],
          featured: row.featured,
          active: row.active,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAllContainers(): Promise<ContainerModel[]> {
    return this.getAll();
  },

  async getByCode(code: string): Promise<ContainerModel | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('container_models')
        .select('*')
        .eq('code', code)
        .eq('active', true)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      if (!data) return null;

      return {
        id: data.id,
        name: data.name,
        code: data.code,
        size: data.size,
        dimensions: data.dimensions,
        price: parseFloat(data.price),
        originalPrice: parseFloat(data.original_price),
        savings: parseFloat(data.savings),
        features: data.features || [],
        images: data.images || [],
        featured: data.featured,
        active: data.active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};